/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';

export type ImageType = {
  url?: string | null;
  description?: string | null;
  width?: number | null;
  height?: number | null;
  useHeightPixelValue?: boolean;
};

type Props = ImageType & {
  shape?: 'square' | 'circle' | 'rounded';
  shouldPreload?: boolean;
  shouldLazyLoad?: boolean;
  style?: Record<string, string>;
};

const styles = {
  lazyImageWrapper: css`
    height: auto;

    // This is to override maxWidth: '95vw' and '90vw' from LazyImgix
    > div {
      max-width: 100%;
    }
  `,
};

function Image({
  width,
  height,
  shape = 'square',
  shouldPreload = false,
  shouldLazyLoad = false,
  style,
  description,
  url,
  useHeightPixelValue,
  ...otherProps
}: Props) {
  const alt = description ?? '';
  const src = url;
  const actualWidth = width === null ? undefined : width;
  const actualHeight = height === null ? undefined : height;

  if (!src) {
    return null;
  }

  let borderRadius = '0';
  if (shape === 'circle') {
    borderRadius = '50%';
  } else if (shape === 'rounded') {
    borderRadius = '25%';
  }

  const imageStyle = {
    maxWidth: '100%',
    height: useHeightPixelValue ? `${height}px` : 'auto',
    borderRadius,
    ...style,
  };

  const imgParams = {
    quality: 40,
  };

  if (shouldPreload) {
    return (
      <ImgixWithPrioritizedLoading
        imgParams={imgParams}
        alt={alt}
        src={src}
        maxWidth={actualWidth}
        maxHeight={actualHeight}
        style={imageStyle}
        {...otherProps}
      />
    );
  }

  if (shouldLazyLoad && actualWidth && actualHeight) {
    return (
      <div css={styles.lazyImageWrapper}>
        <LazyImgix
          src={src}
          alt={alt}
          enableFluidWidth
          maxWidth={actualWidth}
          maxHeight={actualHeight}
          style={imageStyle}
          {...otherProps}
        />
      </div>
    );
  }

  return (
    <Imgix src={src} alt={alt} style={imageStyle} maxWidth={actualWidth} maxHeight={actualHeight} {...otherProps} />
  );
}

export default Image;
